import { minLength, maxLength } from '@vuelidate/validators';
import type { PassengerKey } from '@aviata/booking-data';
import type { Airflow } from '@aviata/platform-types';

import { validationI18n } from '../../../entities/validation-i18n';
import { validationRulesLib } from '../../../shared/lib';

type SchemaValidationProps = {
  passengerKey?: PassengerKey;
  passengerSchema: Airflow.Offer.Schema.PassengerEntity;
  messagePath: string;
};

type AgeConstraints =
  Airflow.Offer.Schema.PassengerEntity['validators']['age_between'];

const mapAgeConstraints = (
  ageConstraints: AgeConstraints,
): validationRulesLib.AgeConstraints => {
  return {
    ...ageConstraints,
    max: ageConstraints.max + 1, // +1 since backend returns inclusive intervals but validation message uses exclusive max
    dateToGetAge: ageConstraints.to_date,
  };
};

/**
 * Method for validation based on a backend passengers schema with i18 support
 * @see https://vuelidate-next.netlify.app/advanced_usage.html#i18n-support
 *
 * @function getSchemaValidations
 * @property {PassengerKey | 'UNK'} passengerType - Что такое passengerType: UNK?:
 * UNK - это сокращение от UNKNOWN т.е UNK указывает на то, что тип пассажира неизвестен
 * Тип пассажира неизвестен в Пассажирах(freedom-passengers) т.к там нет динамический приходящий с бэка валидационной схемы.
 */
export const getSchemaValidations = ({
  passengerKey,
  passengerSchema,
  messagePath,
}: SchemaValidationProps) => {
  const { schema, validators } = passengerSchema;
  const { withI18nMessage } = validationI18n.model.useLocalizedValidators(
    `${messagePath}.schema`,
  );

  const passengerType = passengerKey ? passengerKey.slice(0, 3) : 'UNK';
  const maxAge = validationRulesLib.initMaxAgeValidator(
    mapAgeConstraints(validators.age_between),
    passengerType,
  );
  const minAge = validationRulesLib.initMinAgeValidator(
    mapAgeConstraints(validators.age_between),
    passengerType,
  );

  return {
    firstName: {
      firstNameMinLength: withI18nMessage(
        minLength(schema.first_name.minlength),
        { withArguments: true },
      ),
      firstNameMaxLength: withI18nMessage(
        maxLength(schema.first_name.maxlength),
        { withArguments: true },
      ),
    },
    lastName: {
      lastNameMinLength: withI18nMessage(
        minLength(schema.last_name.minlength),
        { withArguments: true },
      ),
      lastNameMaxLength: withI18nMessage(
        maxLength(schema.last_name.maxlength),
        { withArguments: true },
      ),
    },
    dob: {
      minAge: withI18nMessage(minAge),
      maxAge: withI18nMessage(maxAge),
    },
  };
};
